import { ReactNode, useCallback } from 'react';

import { useModal } from 'mui-modal-provider';

import {
  Button,
  Dialog,
  DialogContent,
  DialogProps,
  HexIcon,
  ShowModalOutput,
  Typography,
  Stack,
} from '@cast/design-system';

export type LeavePageGuardDialogProps = {
  title?: ReactNode;
  subtitle?: ReactNode;
  leaveButtonText?: string;
  onLeave?: () => void;
  onContinue?: () => void;
};

type Props = LeavePageGuardDialogProps &
  Omit<DialogProps, 'title'> & { onClose: () => void };

const LeavePageDialog = ({
  title = 'Are you sure you want to leave without saving?',
  subtitle = 'Any unsaved changes will be lost.',
  leaveButtonText = 'Leave',
  onLeave,
  onContinue,
  ...props
}: Props) => {
  return (
    <Dialog
      {...props}
      PaperProps={{ sx: { overflow: 'visible' } }}
      testId="leave-page-guard"
    >
      <DialogContent
        sx={{
          pt: 0,
          pb: 32,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <HexIcon
          variant="question"
          sx={{ position: 'absolute', transform: 'translateY(-50%)' }}
          size={80}
        />
        <Typography variant="h5" align="center" mt={56} mb={12}>
          {title}
        </Typography>
        <Typography variant="P14R" color="textSecondary" mb={24}>
          {subtitle}
        </Typography>
        <Stack direction="row" gap={16}>
          <Button variant="secondary" onClick={onLeave} testId="leave-button">
            {leaveButtonText}
          </Button>
          <Button onClick={onContinue} testId="continue-button">
            Continue editing
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export const useRouteLeavingConfirmationDialog = (
  dialogProps: LeavePageGuardDialogProps = {}
) => {
  const { showModal } = useModal();

  return useCallback(
    (onAllow: () => void, onDecline: () => void) => {
      const modalInstance: ShowModalOutput = showModal(
        LeavePageDialog,
        {
          ...dialogProps,
          onContinue: () => {
            modalInstance.destroy();
            onDecline();
          },
          onLeave: () => {
            modalInstance.destroy();
            onAllow();
          },
          onClose: () => modalInstance.destroy(),
        },
        { rootId: 'root' }
      );
    },
    [dialogProps, showModal]
  );
};
